import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer, scaleUp } from '../utils/animationVariants';
import CTA from '../components/Cta';
import FAQ from '../components/Faq';
import { useSEO } from '../hooks/useSEO';

const Enterprise = () => {
  useSEO({
    title: "Enterprise Solutions - Team Digital Cards | AM CARDMATE",
    description: "Scalable digital business card solutions for teams and enterprises. Bulk management, analytics, and centralized control for your organization.",
    image: "/assets/enterprise.jpg",
    keywords: "enterprise solutions, team cards, bulk management, corporate digital cards, team networking, business solutions"
  });

  const { ref, controls } = useScrollAnimation(0.1);
  const [activeFeature, setActiveFeature] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({
    employees: 50,
    billingCycle: 'annual' 
  });
  const [selectedMaterial, setSelectedMaterial] = useState('metal');
  const [selectedElements, setSelectedElements] = useState([]);
  const [customQuantity, setCustomQuantity] = useState(100);
  const featuresRef = useRef(null);
  const { ref: featuresAnimRef, controls: featuresControls } = useScrollAnimation(0.1);
  
  const [waitlistForm, setWaitlistForm] = useState({
    email: '',
    company: ''
  });
  const [formStatus, setFormStatus] = useState('idle'); 
  const [formMessage, setFormMessage] = useState('');
  
  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    try {
      const response = await fetch('https://formspree.io/f/xldnlevd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: waitlistForm.email,
          company: waitlistForm.company,
          message: `New Enterprise Waitlist Signup:\n\nEmail: ${waitlistForm.email}\nCompany: ${waitlistForm.company}\nPage: Enterprise Dashboard 2026 Waitlist`
        }),
      });
      
      if (response.ok) {
        setFormStatus('success');
        setFormMessage('Successfully joined the waitlist! We\'ll be in touch with updates.');
        setWaitlistForm({ email: '', company: '' });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Something went wrong. Please try again or contact us directly.');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWaitlistForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateSubscriptionCost = () => {
    const { employees, billingCycle } = subscriptionData;
    let pricePerUser;
    
    if (employees <= 20) {
      pricePerUser = 4.49; 
    } else if (employees <= 50) {
      pricePerUser = 3.99; 
    } else {
      pricePerUser = 3.49; 
    }
    
    if (billingCycle === 'annual') {
      pricePerUser = pricePerUser * 0.8;
    }
    
    const monthlyTotal = employees * pricePerUser;
    const annualTotal = monthlyTotal * 12;
    const monthlyRegularPrice = employees * 4.49; 
    const savings = billingCycle === 'annual' ? (monthlyRegularPrice * 12) - annualTotal : 0;
    
    return {
      pricePerUser,
      monthlyTotal,
      annualTotal,
      savings,
      regularPrice: 4.49
    };
  };

  const calculateCustomPrice = () => {
    let basePrice = selectedMaterial === 'metal' ? 29.99 :
                   selectedMaterial === 'plastic' ? 11.99 :
                   selectedMaterial === 'bamboo' ? 24.99 : 9.99; 
    
    const elementPrices = {
      'logo': 0,
      'qr': 0.99,
      'custom_color': 0
    };
    
    let totalElementPrice = selectedElements.reduce((sum, elementId) => {
      return sum + (elementPrices[elementId] || 0);
    }, 0);
    
    let unitPrice = basePrice + totalElementPrice;
    
    let discount = 0;
    if (customQuantity >= 500) discount = 0.25;
    else if (customQuantity >= 250) discount = 0.15;
    else if (customQuantity >= 100) discount = 0.10;
    
    return {
      unitPrice: unitPrice.toFixed(2),
      discount: discount,
      discountedPrice: (unitPrice * (1 - discount)).toFixed(2),
      totalPrice: (unitPrice * (1 - discount) * customQuantity).toFixed(2)
    };
  };
  
  const subscriptionCost = calculateSubscriptionCost();
  const customPricing = calculateCustomPrice();
  
  const enterpriseFeatures = [
    {
      icon: 'users-cog',
      title: 'User Management Dashboard',
      description: 'Add team members, assign licenses, and manage permissions all from one intuitive interface.',
      gradient: 'from-primary-blue to-primary-blue-dark'
    },
    {
      icon: 'chart-line',
      title: 'Comprehensive Analytics Hub',
      description: 'Monitor your entire team\'s networking performance with detailed insights and smart reports.',
      gradient: 'from-accent-purple to-primary-pink'
    },
    {
      icon: 'id-badge',
      title: 'License Assignment',
      description: 'Instantly assign and revoke card licenses as your team grows or changes.',
      gradient: 'from-accent-green to-accent-teal'
    }
  ];

  const deploymentTimeline = [
    {
      phase: 'Q1 2026',
      title: 'Beta Launch',
      description: 'Early access for select enterprise partners',
      icon: 'rocket',
      status: 'upcoming'
    },
    {
      phase: 'Q2 2026',
      title: 'Full Dashboard Release',
      description: 'Complete user management and analytics platform',
      icon: 'dashboard',
      status: 'upcoming'
    },
    {
      phase: 'Q3 2026',
      title: 'API Integration',
      description: 'Connect with your existing HR and CRM systems',
      icon: 'plug',
      status: 'upcoming'
    },
    {
      phase: 'Q4 2026',
      title: 'Global Availability',
      description: 'Enterprise solution available worldwide',
      icon: 'globe',
      status: 'upcoming'
    }
  ];

  return (
    <>
      <section className="pt-20 md:pt-24 lg:pt-32 pb-16 md:pb-20 bg-gradient-to-br from-dark via-primary-blue-dark to-accent-purple text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-blue/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-accent-purple/20 rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <div className="badge badge-blue inline-flex mb-8 bg-gradient-to-r from-accent-yellow/20 to-primary-pink/20 backdrop-blur-sm border border-accent-yellow/30">
              <FontAwesomeIcon icon="calendar-alt" className="text-accent-yellow" />
              <span className="font-bold">COMING 2026</span>
            </div>
            
            <h1 className="text-h1 text-white mb-6">
  Enterprise Dashboard for <span className="text-gradient-blue-pink">Team Management</span>
</h1>
            
            <p className="text-body-lg text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed">
              A revolutionary platform to manage your entire organization's digital business cards. 
              Add users, assign licenses, and track team-wide networking analytics - all from one 
              powerful dashboard.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto mb-12">
              {[
                { icon: 'users-cog', label: 'Add & Manage Users', desc: 'Bulk import teams' },
                { icon: 'id-badge', label: 'Assign Licenses', desc: 'Instant deployment' },
                { icon: 'chart-line', label: 'Track Analytics', desc: 'Team insights' }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all duration-300"
                >
                  <FontAwesomeIcon icon={feature.icon} className="text-3xl mb-3 text-accent-yellow" />
                  <div className="text-h5 mb-1">{feature.label}</div>
                  <div className="text-body-sm text-white/80">{feature.desc}</div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => document.getElementById('waitlist-form').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-accent-yellow to-primary-pink text-dark px-10 py-4 rounded-full font-bold text-body-lg hover:shadow-2xl transition-all duration-300 group flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <FontAwesomeIcon icon="bell" />
                <span>Join 2026 Waitlist</span>
                <FontAwesomeIcon 
                  icon="arrow-right" 
                  className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                />
              </button>
              
              <a 
                href="#pricing-calculator" 
                className="btn btn-outline border-2 border-white text-white hover:bg-white hover:text-dark btn-lg w-full sm:w-auto px-8 py-4"
              >
                <FontAwesomeIcon icon="calculator" />
                <span>Calculate Savings</span>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden bg-gradient-to-b from-light to-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(77,162,255,0.06)_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 lg:mb-16">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="desktop" />
              <span>Dashboard Preview</span>
            </div>
            
            <h2 className="text-h1 mb-6">
              Manage Your Team with <span className="text-gradient-blue-pink">One Dashboard</span>
            </h2>
            
            <p className="text-body-lg text-gray leading-relaxed">
              Everything you need to deploy and manage digital business cards across your organization
            </p>
          </div>
          
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-primary-blue to-accent-purple p-0.5 sm:p-1">
              <div className="rounded-2xl sm:rounded-3xl bg-white">
                <div className="bg-gradient-to-r from-primary-blue to-accent-purple text-white p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-center sm:text-left">
                      <h3 className="text-h4 mb-1">Enterprise Dashboard</h3>
                      <p className="text-body-sm text-white/80">Manage your team's digital presence</p>
                    </div>
                    <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
                      <button className="bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg hover:bg-white/30 transition-colors text-body-sm">
                        <FontAwesomeIcon icon="plus" className="mr-1 sm:mr-2" />
                        <span className="hidden xs:inline">Add </span>User
                      </button>
                      <button className="bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg hover:bg-white/30 transition-colors text-body-sm">
                        <FontAwesomeIcon icon="download" className="mr-1 sm:mr-2" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-3 sm:p-6 lg:p-8">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                    <div className="bg-gradient-to-br from-primary-blue/10 to-primary-blue/5 rounded-xl p-3 sm:p-4 lg:p-6">
                      <FontAwesomeIcon icon="users" className="text-lg sm:text-xl lg:text-2xl text-primary-blue mb-2 sm:mb-3" />
                      <div className="text-h3 text-dark mb-1">1,247</div>
                      <div className="text-body-sm text-gray">Active Users</div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-accent-purple/10 to-accent-purple/5 rounded-xl p-3 sm:p-4 lg:p-6">
                      <FontAwesomeIcon icon="id-badge" className="text-lg sm:text-xl lg:text-2xl text-accent-purple mb-2 sm:mb-3" />
                      <div className="text-h3 text-dark mb-1">1,500</div>
                      <div className="text-body-sm text-gray">Licenses Available</div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-accent-green/10 to-accent-green/5 rounded-xl p-3 sm:p-4 lg:p-6">
                      <FontAwesomeIcon icon="handshake" className="text-lg sm:text-xl lg:text-2xl text-accent-green mb-2 sm:mb-3" />
                      <div className="text-h3 text-dark mb-1">8,432</div>
                      <div className="text-body-sm text-gray">Connections Made</div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-primary-pink/10 to-primary-pink/5 rounded-xl p-3 sm:p-4 lg:p-6">
                      <FontAwesomeIcon icon="chart-line" className="text-lg sm:text-xl lg:text-2xl text-primary-pink mb-2 sm:mb-3" />
                      <div className="text-h3 text-dark mb-1">+32%</div>
                      <div className="text-body-sm text-gray">Network Growth</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-3 sm:p-4 lg:p-6">
                    <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-3 sm:gap-4 mb-4">
                      <h4 className="text-h5 text-dark">Team Members</h4>
                      <div className="flex gap-2 self-end xs:self-auto">
                        <button className="text-primary-blue hover:text-primary-blue-dark p-2 hover:bg-blue-50 rounded-lg transition-colors">
                          <FontAwesomeIcon icon="filter" />
                        </button>
                        <button className="text-primary-blue hover:text-primary-blue-dark p-2 hover:bg-blue-50 rounded-lg transition-colors">
                          <FontAwesomeIcon icon="search" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {[
                        { name: 'John Smith', role: 'Sales Director', connections: 342, status: 'active', initials: 'JS' },
                        { name: 'Sarah Chen', role: 'Marketing Lead', connections: 287, status: 'active', initials: 'SC' },
                        { name: 'Mike Johnson', role: 'Account Manager', connections: 195, status: 'inactive', initials: 'MJ' }
                      ].map((user, index) => (
                        <div 
                          key={index}
                          className="bg-white rounded-lg p-3 sm:p-4 flex flex-col xs:flex-row xs:items-center gap-3 sm:gap-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center gap-3 sm:gap-4 flex-1">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-body-sm flex-shrink-0 ${
                              index === 0 ? 'bg-gradient-to-br from-primary-blue to-accent-purple' :
                              index === 1 ? 'bg-gradient-to-br from-primary-pink to-accent-yellow' :
                              'bg-gradient-to-br from-gray-400 to-gray-600'
                            }`}>
                              {user.initials}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-body font-semibold text-dark truncate">{user.name}</div>
                              <div className="text-body-sm text-gray truncate">{user.role}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between xs:justify-end xs:gap-4 lg:gap-6">
                            <div className="text-center">
                              <div className="text-body font-bold text-dark">{user.connections}</div>
                              <div className="text-caption text-gray">connections</div>
                            </div>
                            <div className={`px-2 sm:px-3 py-1 rounded-full text-caption font-medium ${
                              user.status === 'active' 
                                ? 'bg-accent-green/10 text-accent-green' 
                                : 'bg-gray-200 text-gray'
                            }`}>
                              {user.status}
                            </div>
                            <button className="text-gray hover:text-primary-blue p-1 hover:bg-blue-50 rounded transition-colors">
                              <FontAwesomeIcon icon="ellipsis-v" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 text-center">
                      <button className="text-primary-blue hover:text-primary-blue-dark font-medium text-body-sm px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors">
                        View All Team Members
                        <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-light">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="badge badge-purple inline-flex mb-4">
              <FontAwesomeIcon icon="sparkles" />
              <span>Core Features</span>
            </div>
            
            <h2 className="text-h1 mb-6">
              Everything You Need in <span className="text-gradient-blue-pink">One Platform</span>
            </h2>
            
            <p className="text-body-lg text-gray">
              Simple, powerful tools to manage your team's digital business cards
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {enterpriseFeatures.map((feature, index) => (
              <div 
                key={index}
                className="group relative"
              >
                <div className="bg-white rounded-3xl p-8 h-full shadow-md hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-6 shadow-lg`}>
                    <FontAwesomeIcon icon={feature.icon} className="text-3xl" />
                  </div>
                  
                  <h3 className="text-h4 text-dark mb-4">{feature.title}</h3>
                  <p className="text-body text-gray leading-relaxed">{feature.description}</p>
                  
                  <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${feature.gradient}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section id="pricing-calculator" className="py-16 md:py-24 bg-gradient-to-b from-light to-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(77,162,255,0.06)_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="tags" />
              <span>Exclusive Pricing</span>
            </div>
            
            <h2 className="text-h1 mb-6">
              Calculate Your <span className="text-gradient-blue-pink">Enterprise Savings</span>
            </h2>
            
            <p className="text-body-lg text-gray">
              Special launch pricing for early adopters - Save up to 67%
            </p>
          </div>
          
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-lg relative overflow-hidden">
                <div className="absolute top-10 sm:top-4 right-2 sm:right-4 z-10">
                  <div className="bg-gradient-to-r from-accent-yellow to-primary-pink text-dark px-2 sm:px-4 py-1 sm:py-2 rounded-full text-body-sm font-bold shadow-lg">
                    67% OFF
                  </div>
                </div>
                
                <h3 className="text-h4 text-dark mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-blue to-primary-blue-dark rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="users" />
                  </div>
                  <span>Subscription Pricing</span>
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="flex justify-between items-center mb-3">
                      <span className="text-body font-semibold text-dark">Number of Employees</span>
                      <span className="text-h4 text-primary-blue">{subscriptionData.employees}</span>
                    </label>
                    <div className="relative">
                      <input 
                        type="range" 
                        min="10" 
                        max="500" 
                        value={subscriptionData.employees}
                        onChange={(e) => setSubscriptionData({...subscriptionData, employees: parseInt(e.target.value)})}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #4DA2FF 0%, #4DA2FF ${(subscriptionData.employees - 10) / 490 * 100}%, #DDE5F2 ${(subscriptionData.employees - 10) / 490 * 100}%, #DDE5F2 100%)`
                        }}
                      />
                      <div className="flex justify-between text-body-sm text-gray mt-1">
                        <span>10</span>
                        <span>500+</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-body font-semibold text-dark mb-3 block">Billing Cycle</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        className={`py-3 px-4 rounded-xl font-medium transition-all text-body ${
                          subscriptionData.billingCycle === 'monthly' 
                            ? 'bg-gradient-to-r from-primary-blue to-primary-blue-dark text-white' 
                            : 'bg-gray-light text-gray-dark hover:bg-gray-200'
                        }`}
                        onClick={() => setSubscriptionData({...subscriptionData, billingCycle: 'monthly'})}
                      >
                        Monthly
                      </button>
                      <button
                        className={`py-3 px-4 rounded-xl font-medium transition-all relative text-body ${
                          subscriptionData.billingCycle === 'annual' 
                            ? 'bg-gradient-to-r from-primary-blue to-primary-blue-dark text-white' 
                            : 'bg-gray-light text-gray-dark hover:bg-gray-200'
                        }`}
                        onClick={() => setSubscriptionData({...subscriptionData, billingCycle: 'annual'})}
                      >
                        Annual
                        {subscriptionData.billingCycle === 'annual' && (
                          <span className="absolute -top-2 -right-2 bg-accent-green text-white text-xs px-2 py-1 rounded-full animate-pulse">
                            Save More!
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-primary-blue/5 to-accent-purple/5 rounded-2xl p-6 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-body text-gray">Regular price per user</span>
                      <span className="line-through text-gray text-body">£8.99/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-body text-gray">Your price per user</span>
                      <span className="text-h5 text-accent-green">£{subscriptionCost.pricePerUser.toFixed(2)}/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-body text-gray">Monthly total</span>
                      <span className="text-h5 text-dark">£{subscriptionCost.monthlyTotal.toFixed(2)}</span>
                    </div>
                    {subscriptionData.billingCycle === 'annual' && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-body text-gray">Annual total</span>
                          <span className="text-h5 text-dark">£{subscriptionCost.annualTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-accent-green">
                          <span className="text-body">You save</span>
                          <span className="text-h4">£{subscriptionCost.savings.toFixed(2)}/year</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="bg-gradient-to-r from-accent-yellow/10 to-primary-pink/10 rounded-xl p-4 flex items-center gap-3">
                    <FontAwesomeIcon icon="clock" className="text-accent-yellow text-xl" />
                    <div>
                      <p className="text-body-sm font-semibold text-dark">Launching Q1 2026</p>
                      <p className="text-caption text-gray">Lock in this special pricing now</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-lg relative overflow-hidden">
                <div className="absolute top-16 sm:top-4 right-2 sm:right-4 z-10">
                  <div className="bg-gradient-to-r from-accent-purple to-primary-pink text-white px-2 sm:px-4 py-1 sm:py-2 rounded-full text-body-sm font-bold shadow-lg">
                    BULK
                  </div>
                </div>
                
                <h3 className="text-h4 text-dark mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-accent-purple to-primary-pink rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <FontAwesomeIcon icon="credit-card" />
                  </div>
                  <span>Custom Cards & Accessories</span>
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="text-body font-semibold text-dark mb-3 block">Choose Product</label>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { id: 'metal', name: 'Metal Card', icon: 'shield-alt', color: 'from-blue-400 to-blue-600', price: 29.99, min: 100 },
                        { id: 'plastic', name: 'Premium Plastic', icon: 'credit-card', color: 'from-dark to-gray-dark', price: 11.99, min: 100 },
                        { id: 'bamboo', name: 'Eco Bamboo', icon: 'leaf', color: 'from-accent-yellow to-green-600', price: 24.99, min: 100 },
                        { id: 'bracelet', name: 'NFC Bracelet', icon: 'circle', color: 'from-primary-blue to-accent-purple', price: 9.99, min: 100 }
                      ].map((material) => (
                        <button
                          key={material.id}
                          className="p-4 rounded-xl border-2 transition-all relative overflow-hidden"
                          style={{
                            borderColor: selectedMaterial === material.id ? '#4DA2FF' : '#DDE5F2',
                            backgroundColor: selectedMaterial === material.id ? 'rgba(77, 162, 255, 0.05)' : 'white'
                          }}
                          onClick={() => setSelectedMaterial(material.id)}
                        >
                          <div className={`w-12 h-12 bg-gradient-to-br ${material.color} rounded-lg flex items-center justify-center text-white mb-3 mx-auto`}>
                            <FontAwesomeIcon icon={material.icon} className="text-lg" />
                          </div>
                          <div className="text-body-sm font-medium text-dark">{material.name}</div>
                          <div className="text-caption text-gray mt-1">£{material.price}</div>
                          <div className="text-caption text-primary-blue font-medium">Min: {material.min} pcs</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-body font-semibold text-dark mb-3 block">Add Customization</label>
                    <div className="space-y-3">
                      {[
                        { id: 'logo', name: 'Company Logo Engraving', price: 0, free: true },
                        { id: 'qr', name: 'Custom QR Code', price: 0.99 },
                        { id: 'custom_color', name: 'Custom Brand Colors', price: 0, free: true }
                      ].map((element) => (
                        <label key={element.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox"
                              checked={selectedElements.includes(element.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedElements([...selectedElements, element.id]);
                                } else {
                                  setSelectedElements(selectedElements.filter(el => el !== element.id));
                                }
                              }}
                              className="w-5 h-5 text-primary-blue rounded"
                            />
                            <span className="text-body font-medium text-dark">{element.name}</span>
                          </div>
                          {element.free ? (
                            <span className="text-accent-green font-semibold text-body-sm">FREE</span>
                          ) : (
                            <span className="text-body-sm text-gray">+£{element.price}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="flex justify-between items-center mb-3">
                      <span className="text-body font-semibold text-dark">Quantity</span>
                      <span className="text-h4 text-accent-purple">{customQuantity}</span>
                    </label>
                    <div className="relative">
                      <input 
                        type="range" 
                        min="100" 
                        max="1000" 
                        step="50"
                        value={customQuantity}
                        onChange={(e) => setCustomQuantity(parseInt(e.target.value))}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${(customQuantity - 100) / 900 * 100}%, #DDE5F2 ${(customQuantity - 100) / 900 * 100}%, #DDE5F2 100%)`
                        }}
                      />
                      <div className="flex justify-between text-body-sm text-gray mt-1">
                        <span>100</span>
                        <span>1000</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-accent-purple/5 to-primary-pink/5 rounded-2xl p-6 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-body text-gray">Unit price</span>
                      <span className="text-body font-bold">£{customPricing.unitPrice}</span>
                    </div>
                    
                    {customPricing.discount > 0 && (
                      <div className="flex justify-between items-center text-accent-green">
                        <span className="text-body">Volume discount ({(customPricing.discount * 100).toFixed(0)}% OFF)</span>
                        <span className="text-body font-bold">£{customPricing.discountedPrice}/unit</span>
                      </div>
                    )}
                    
                    <div className="border-t pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-h5 text-dark">Total Investment</span>
                        <div>
                          {customPricing.discount > 0 && (
                            <span className="line-through text-gray text-body-sm mr-2">
                              £{(customPricing.unitPrice * customQuantity).toFixed(2)}
                            </span>
                          )}
                          <span className="text-h4 text-gradient-blue-pink">
                            £{customPricing.totalPrice}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-primary-blue/10 to-accent-teal/10 rounded-xl p-4">
                    <h4 className="text-body font-semibold text-dark mb-2">Volume Pricing Tiers</h4>
                    <ul className="text-body-sm text-gray-dark space-y-1">
                      <li className={customQuantity >= 100 && customQuantity < 250 ? 'text-primary-blue font-semibold' : ''}>
                        • 100-249: 10% discount
                      </li>
                      <li className={customQuantity >= 250 && customQuantity < 500 ? 'text-primary-blue font-semibold' : ''}>
                        • 250-499: 15% discount
                      </li>
                      <li className={customQuantity >= 500 ? 'text-primary-blue font-semibold' : ''}>
                        • 500+: 25% discount (Best value!)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-16 md:py-20 bg-gradient-to-br from-dark via-primary-blue-dark to-accent-purple text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-blue/20 rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="badge badge-blue inline-flex mb-6 bg-gradient-to-r from-accent-yellow/20 to-primary-pink/20 backdrop-blur-sm border border-accent-yellow/30">
              <FontAwesomeIcon icon="road" className="text-accent-yellow" />
             <span className="text-white">2026 Roadmap</span>
            </div>
            
           <h2 className="text-h2 text-white">
  Launch Timeline
</h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {deploymentTimeline.map((item, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:border-white/40 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-accent-yellow to-primary-blue rounded-xl flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={item.icon} className="text-white text-lg" />
                  </div>
                  <div className="text-accent-yellow font-bold text-body-sm mb-2">{item.phase}</div>
                  <h3 className="text-h5 mb-2">{item.title}</h3>
                  <p className="text-body-sm text-white/80 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="waitlist-form" className="py-16 md:py-24 bg-gradient-to-br from-white to-light relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(77,162,255,0.08)_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="badge badge-blue inline-flex mb-6 bg-gradient-to-r from-accent-yellow to-primary-pink text-dark">
              <FontAwesomeIcon icon="clock" />
              <span className="font-bold">Limited Spots for 2026 Launch</span>
            </div>
            
            <h2 className="text-h1 mb-6 text-dark">
              Be First When We Launch in <span className="text-gradient-blue-pink">2026</span>
            </h2>
            
            <p className="text-body-lg text-gray mb-10 max-w-3xl mx-auto">
              Join 500+ forward-thinking companies already on the waitlist. Get exclusive early 
              access, special pricing, and shape the future of enterprise networking.
            </p>
            
            <form onSubmit={handleWaitlistSubmit} className="max-w-2xl mx-auto mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <input 
                  type="email" 
                  name="email"
                  value={waitlistForm.email}
                  onChange={handleInputChange}
                  placeholder="Work Email Address" 
                  className="flex-1 px-6 py-4 rounded-full border-2 border-gray-light focus:border-primary-blue outline-none transition-colors text-body"
                  required
                  disabled={formStatus === 'submitting'}
                />
                <input 
                  type="text" 
                  name="company"
                  value={waitlistForm.company}
                  onChange={handleInputChange}
                  placeholder="Company Name" 
                  className="flex-1 px-6 py-4 rounded-full border-2 border-gray-light focus:border-primary-blue outline-none transition-colors text-body"
                  required
                  disabled={formStatus === 'submitting'}
                />
                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="bg-gradient-to-r from-primary-blue to-accent-purple text-white px-8 py-4 rounded-full font-bold text-body-lg shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl transition-all duration-300"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="bell" className="mr-2" />
                      Join 2026 Waitlist
                    </>
                  )}
                </button>
              </div>
              
              {formStatus === 'success' && (
                <div className="mt-4 p-4 bg-accent-green/10 border border-accent-green/20 rounded-xl text-accent-green text-body">
                  <FontAwesomeIcon icon="check-circle" className="mr-2" />
                  {formMessage}
                </div>
              )}
              
              {formStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-body">
                  <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                  {formMessage}
                </div>
              )}
            </form>
            
            <div className="flex flex-wrap justify-center gap-6 text-body-sm text-gray">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="check-circle" className="text-accent-green" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="gift" className="text-primary-pink" />
                <span>Exclusive launch perks</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="headset" className="text-primary-blue" />
                <span>Priority onboarding</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <FAQ />
    </>
  );
};

export default Enterprise;